import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { fromIsoDate, getUpcomingBillingCount, toIsoDate } from '../data/mockUpcomingBillings';
interface SubscriptionCalendarProps {
  // Day to open on and briefly highlight (YYYY-MM-DD)
  focusDate?: string | null;
  onSelectDay: (isoDate: string) => void;
}
const WEEKDAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export function SubscriptionCalendar({
  focusDate,
  onSelectDay
}: SubscriptionCalendarProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayIso = toIsoDate(today);
  const initialMonth = focusDate ? fromIsoDate(focusDate) : today;
  const [visibleMonth, setVisibleMonth] = useState(new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));
  const [highlightedDate, setHighlightedDate] = useState<string | null>(focusDate ?? null);

  // Jump to and briefly highlight a newly focused day
  useEffect(() => {
    if (!focusDate) return;
    const date = fromIsoDate(focusDate);
    setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    setHighlightedDate(focusDate);
    const timer = window.setTimeout(() => setHighlightedDate(null), 2000);
    return () => window.clearTimeout(timer);
  }, [focusDate]);

  // Six rows starting from the Sunday on or before the 1st
  const gridStart = new Date(visibleMonth);
  gridStart.setDate(1 - visibleMonth.getDay());
  const cells = Array.from({
    length: 42
  }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return date;
  });
  const shiftMonth = (delta: number) => {
    setVisibleMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };
  const monthLabel = visibleMonth.toLocaleDateString('en-AU', {
    month: 'long',
    year: 'numeric'
  });
  return <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-5643" data-mp="1kms3h6vtlf0l">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-4" data-id="element-5644" data-mp="1fo7397gkivc5">
        <div className="flex items-center gap-2" data-id="element-5645" data-mp="de6ujwwxj699">
          <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month" className="w-9 h-9 flex items-center justify-center rounded-md border border-[#CBD5E1] text-[#475569] hover:bg-[#F8FAFC] transition-colors" data-id="element-5646" data-mp="ibgonpv9k63f">
            <ChevronLeft className="w-4 h-4" data-id="element-5647" data-mp="r4ts3lqyeq3r" />
          </button>
          <h3 className="text-base font-semibold text-[#0F172A] min-w-[160px] text-center" aria-live="polite" data-id="element-5648" data-mp="w3dchhpwftu0">
            {monthLabel}
          </h3>
          <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month" className="w-9 h-9 flex items-center justify-center rounded-md border border-[#CBD5E1] text-[#475569] hover:bg-[#F8FAFC] transition-colors" data-id="element-5649" data-mp="110n3h1sxsdjy">
            <ChevronRight className="w-4 h-4" data-id="element-5650" data-mp="1a0js53fbtcf0" />
          </button>
        </div>
        <button type="button" onClick={() => setVisibleMonth(new Date(today.getFullYear(), today.getMonth(), 1))} className="h-9 px-3 rounded-md border border-[#CBD5E1] text-sm font-medium text-[#475569] hover:bg-[#F8FAFC] transition-colors" data-id="element-5651" data-mp="37uh4voi0fggn">
          Today
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 border-t border-l border-[#E2E8F0]" role="grid" aria-label={monthLabel} data-id="element-5652" data-mp="2zhx8juak84e4">
        {WEEKDAY_HEADERS.map(day => <div key={day} role="columnheader" className="border-r border-b border-[#E2E8F0] bg-[#F8FAFC] px-2 py-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider text-center" data-id="element-5653" data-mp="2qjv93j6h5jsy">
            {day}
          </div>)}

        {cells.map(date => {
        const isoDate = toIsoDate(date);
        const inMonth = date.getMonth() === visibleMonth.getMonth();
        const isToday = isoDate === todayIso;
        const isPast = date < today;
        const count = isPast ? 0 : getUpcomingBillingCount(date);
        const isClickable = !isPast && count > 0;
        const isHighlighted = highlightedDate === isoDate;
        const pillClass = count >= 10 ? 'bg-[#0EA5E9] text-white' : count >= 5 ? 'bg-[#BAE6FD] text-[#0369A1]' : 'bg-[#E0F2FE] text-[#0369A1]';
        const dateNumberClass = !inMonth ? 'text-[#CBD5E1]' : isPast ? 'text-[#94A3B8]' : 'text-[#0F172A]';
        const cellClass = `relative min-h-[84px] border-r border-b border-[#E2E8F0] p-2 text-left transition-colors ${inMonth ? 'bg-white' : 'bg-[#F8FAFC]'} ${isToday ? 'ring-2 ring-inset ring-[#0EA5E9]' : ''} ${isHighlighted ? 'bg-[#F0F9FF] ring-2 ring-inset ring-[#7DD3FC]' : ''}`;
        const content = <>
              <span className={`text-sm font-medium ${dateNumberClass}`} data-id="element-5654" data-mp="dc2hdggffepz">
                {date.getDate()}
              </span>
              {!isPast && count > 0 && <span className={`mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${pillClass}`} data-id="element-5655" data-mp="3vgw0doogy3pz">
                  {count} due
                </span>}
            </>;
        return isClickable ? <button key={isoDate} type="button" role="gridcell" onClick={() => onSelectDay(isoDate)} aria-label={`${date.toLocaleDateString('en-AU', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        })}: ${count} due`} className={`${cellClass} flex flex-col items-start hover:bg-[#F0F9FF] cursor-pointer`} data-id="element-5656" data-mp="2k43bcln2btdm">
                {content}
              </button> : <div key={isoDate} role="gridcell" className={`${cellClass} flex flex-col items-start`} data-id="element-5657" data-mp="2qhqrmqixv63v">
                {content}
              </div>;
      })}
      </div>

      <p className="text-xs text-[#94A3B8] mt-4 m-0" data-id="element-5658" data-mp="1xstknjk6gvqf">
        Shows subscriptions due to bill on each day, in your account timezone.
        Paused subscriptions aren't counted.
      </p>
    </div>;
}